'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ImagePreviewOverlay } from './ImagePreviewOverlay'
import { SexIcon } from './SexIcon'
import { avatarDisplaySrc } from '@/lib/utils'

export type ContactProfilePopoverProps = {
  anchorRect: DOMRect | null
  avatar: string
  latestAvatarUrl?: string
  displayName: string
  wxid: string
  remark?: string
  nickName?: string
  alias?: string
  region?: string
  signature?: string
  sex?: string
  actionLabel?: string
  actionLoading?: boolean
  onAction?: () => void
  onClose: () => void
}

const POPOVER_WIDTH = 288
const POPOVER_OFFSET = 8

export function ContactProfilePopover({
  anchorRect,
  avatar,
  latestAvatarUrl,
  displayName,
  wxid,
  remark,
  nickName,
  alias,
  region,
  signature,
  sex,
  actionLabel,
  actionLoading = false,
  onAction,
  onClose,
}: ContactProfilePopoverProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewSrc, setPreviewSrc] = useState(avatar)
  const displayAvatar = avatarDisplaySrc(avatar, wxid || displayName)

  useLayoutEffect(() => {
    if (!anchorRect) {
      setPosition(null)
      return
    }
    const popoverHeight = wrapperRef.current?.offsetHeight ?? 320
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    let top = anchorRect.bottom + POPOVER_OFFSET
    if (top + popoverHeight > viewportHeight - 8) {
      top = Math.max(8, anchorRect.top - popoverHeight - POPOVER_OFFSET)
    }
    let left = anchorRect.left + anchorRect.width / 2 - POPOVER_WIDTH / 2
    left = Math.max(8, Math.min(left, viewportWidth - POPOVER_WIDTH - 8))
    setPosition({ top, left })
  }, [anchorRect])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    function onDocumentMouseDown(event: globalThis.MouseEvent) {
      if (previewOpen) return
      if (wrapperRef.current && wrapperRef.current.contains(event.target as Node)) return
      onClose()
    }

    function onScroll() {
      if (previewOpen) return
      onClose()
    }

    document.addEventListener('mousedown', onDocumentMouseDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [onClose, previewOpen])

  return (
    <>
      <div
        ref={wrapperRef}
        className="fixed z-[300] w-[280px] bg-white border border-hair shadow-lg rounded-lg overflow-hidden"
        style={{
          top: position?.top ?? -9999,
          left: position?.left ?? -9999,
          visibility: position ? 'visible' : 'hidden',
        }}
        role="dialog"
        aria-label="联系人资料"
      >
        {/* Header with gradient background */}
        <div className="relative bg-gradient-to-br from-accent/10 to-blue-50 p-4 pb-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="查看头像大图"
              className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-white shadow-sm border border-hair/50"
              onClick={() => {
                setPreviewSrc(latestAvatarUrl || displayAvatar)
                setPreviewOpen(true)
              }}
            >
              <img src={displayAvatar} alt={displayName} className="w-full h-full object-cover" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-base font-medium text-ink truncate flex items-center gap-1.5">
                <span className="truncate">{displayName}</span>
                <SexIcon sex={sex}/>
              </div>
              {remark && remark !== nickName && nickName && (
                <div className="text-xs text-soft truncate">昵称：{nickName}</div>
              )}
              <div className="text-xs text-muted truncate mt-0.5">{wxid}</div>
            </div>
          </div>
        </div>

        {/* Info rows */}
        <div className="p-3 space-y-2">
          {alias && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted shrink-0 w-10">微信号</span>
              <span className="text-ink truncate">{alias}</span>
            </div>
          )}
          {remark && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted shrink-0 w-10">备注</span>
              <span className="text-ink truncate">{remark}</span>
            </div>
          )}
          {region && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted shrink-0 w-10">地区</span>
              <span className="text-ink truncate">{region}</span>
            </div>
          )}
          {signature && (
            <div className="pt-2 border-t border-hair/70">
              <p className="text-xs text-soft leading-relaxed line-clamp-3">{signature}</p>
            </div>
          )}
          {!alias && !remark && !region && !signature && (
            <div className="text-xs text-muted text-center py-1">暂无更多信息</div>
          )}
          {actionLabel ? (
            <button
              type="button"
              disabled={actionLoading || !onAction}
              onClick={onAction}
              className="mt-2 h-9 w-full rounded-md bg-accent text-sm font-medium text-white transition-opacity disabled:opacity-60"
            >
              {actionLabel}
            </button>
          ) : null}
        </div>
      </div>
      {previewOpen ? <ImagePreviewOverlay src={previewSrc} onClose={() => setPreviewOpen(false)} /> : null}
    </>
  )
}
